import { useAuth0 } from '@auth0/auth0-react';
import {
  useAssignResourcesHomeSite,
  useCreateResource,
  useDeleteResource,
  useResources,
  useSitesWithMembers,
  useUpdateResource,
} from '@my/directory-api/hooks';
import type { ResourceCheckAssignment } from '@my/check-api';
import type {
  CreateResourceRequest,
  Resource,
  ResourceTypeValue,
  UpdateResourceRequest,
} from '@my/directory-api';
import { ConfirmDialog, ErrorBanner, Loading, PageHeader, SelectInput, showToast } from '@my/ui';
import { BadgeCheck, ChevronDown, ChevronUp, ChevronsUpDown, Edit, Plus, Search } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCheckTemplates,
  useResourceCheckAssignments,
  useSetResourceCheckAssignments,
} from '@my/check-api/hooks';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { ResourceEditor } from './ResourceEditor';
import { emptyDraft, toDraft, type ResourceDraft } from './lib/resourceDraft';
import {
  checkSummaryFor,
  useResourceCheckSummaries,
  type CheckStatusValue,
  type ResourceCheckSummary,
} from './lib/checkSummary';

// ────────────────────────────────────────────────────────────────────────────
// Resources page — Check-side port of the TMS Resources screen, narrowed
// to what a walk-around check actually concerns: the drivers who perform
// checks and the vehicles they check.
//
// Differences from the TMS original, all deliberate:
//   - Types are grouped into Drivers / Vehicles (Truck + Rigid) rather
//     than one chip per raw type. Trailers and plant ("Other") are out of
//     scope for now and are excluded from the list entirely.
//   - TMS's Compliance / Compliant columns and their filter chips are
//     gone — they roll up Directory certifications, which is a TMS
//     concern. In their place sit Last Check and Status, the walk-around
//     roll-up (see `lib/checkSummary.ts`).
//   - No service-tag chip row: Check has no service-tag library yet.
//     Predefined certs from the shared Directory catalogue still work
//     exactly as they do in TMS.
//
// Resources are shared master data owned by My.Directory, not by any one
// app — the rows here are the same rows TMS and Depot edit.
//
// Org id resolution pulls from the JWT claim until per-org switching
// lands in the shared account store.
// ────────────────────────────────────────────────────────────────────────────

/** Types rolled up under the "Vehicles" chip. */
const VEHICLE_TYPES: ResourceTypeValue[] = ['Truck', 'Rigid', 'Trailer'];

/** The Drivers grid manages exactly one type. */
const DRIVER_TYPES: ResourceTypeValue[] = ['Driver'];

/**
 * Resource types this app manages, in default row order. Trailers and
 * plant ("Other") are deliberately out of scope for now: they're filtered
 * out of the list rather than shown behind a chip, so the count in the
 * grid always matches what the chips can reach. Widening this array (plus
 * the editor's own copy) is all that's needed to bring them back.
 */
const TYPE_ORDER: ResourceTypeValue[] = ['Driver', 'Truck', 'Rigid', 'Trailer'];

/** Sentinel in the bulk site picker meaning "unassign the site". */
const CLEAR_SITE = '__clear__';

/** Sentinel in the bulk template picker meaning "remove the template". */
const CLEAR_TEMPLATE = '__clear__';

type SortKey = 'name' | 'type' | 'lastCheck';
type SortDir = 'asc' | 'desc';
type StatusFilter = 'all' | CheckStatusValue;

/** Chip order for the status filter row. */
const CHECK_STATUS_ORDER: CheckStatusValue[] = ['Pass', 'Defect', 'Advisory', 'NotDone'];

/** Status pill palette. `NotDone` stays neutral — it's an absence, not a fault. */
const CHECK_STATUS_CLASS: Record<CheckStatusValue, string> = {
  Pass: 'bg-green-100 text-green-700',
  Defect: 'bg-red-100 text-red-700',
  Advisory: 'bg-amber-100 text-amber-800',
  NotDone: 'bg-slate-100 text-slate-500',
};

/** Saturated dot for the filter chips — the pill palette is too pale at 6px. */
const CHECK_STATUS_DOT: Record<CheckStatusValue, string> = {
  Pass: 'bg-green-500',
  Defect: 'bg-red-500',
  Advisory: 'bg-amber-400',
  NotDone: 'bg-slate-400',
};

/**
 * Which half of the fleet this instance manages. Drivers and vehicles were one
 * screen until the Last Check column gave the split away: for a driver it is
 * the check they PERFORMED, for a vehicle the check it RECEIVED. One column,
 * two meanings, plus diverging actions — templates on vehicles, app access on
 * drivers.
 *
 * Kept as one parameterised component rather than two forks: the grid is ~850
 * lines and is already a copy of the TMS one, so a third copy would mean every
 * fix landing in three places.
 */
export type ResourceGridMode = 'drivers' | 'vehicles';

export function ResourceGridPage({ mode }: { mode: ResourceGridMode }) {
  const isVehicles = mode === 'vehicles';
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  // Multi-namespace: 'resources' is the primary lookup, 'common' is the
  // fallback for shared verbs / state words (`actions.add`, `states.*`).
  // The explicit `common:…` prefix below makes those references obvious
  // at the call site so future moves are easy to grep.
  const { t } = useTranslation(['resources', 'common']);

  const listQuery = useResources(orgId);
  // Walk-around roll-ups per resource, live from the Check API. A resource
  // with no submitted check has no entry and resolves to "Not done".
  // See lib/checkSummary.ts.
  const checkSummaries = useResourceCheckSummaries(orgId);
  const createMutation = useCreateResource();
  const updateMutation = useUpdateResource();
  const deleteMutation = useDeleteResource();

  const isBusy =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // Editor state. `null` = no editor open; `'new'` = drafting a fresh row;
  // a guid = editing the row with that id. Same triple-state pattern as
  // CargoDefinitionsSection / ChargesAndCostsSection.
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<ResourceDraft>(emptyDraft());
  const [pendingDelete, setPendingDelete] = useState<Resource | null>(null);

  // Pre-compute label dictionaries once per render. The typed-keys i18n
  // augmentation doesn't accept template-literal keys (every key has to
  // be a static string-literal it can prove exists in the JSON), so
  // dynamic lookups go through these maps instead of `t(`types.${x}`)`.
  const typeLabels: Record<ResourceTypeValue, string> = {
    Driver: t('types.driver'),
    Truck: t('types.truck'),
    Rigid: t('types.rigid'),
    Trailer: t('types.trailer'),
    Other: t('types.other'),
  };
  const checkStatusLabels: Record<CheckStatusValue, string> = {
    Pass: t('status.pass'),
    Defect: t('status.defect'),
    Advisory: t('status.advisory'),
    NotDone: t('status.notDone'),
  };

  // ── UI state ─────────────────────────────────────────────────────────────

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('all');

  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // ── Bulk site assignment ────────────────────────────────────────────────

  const sitesQuery = useSitesWithMembers(orgId);
  const sites = useMemo(
    () => (sitesQuery.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [sitesQuery.data],
  );
  const siteNameById = useMemo(() => new Map(sites.map((s) => [s.id, s.name])), [sites]);

  // Template assignment. Fetched whole — one row per vehicle at most, so the
  // grid joins client-side rather than querying per row.
  const assignmentsQuery = useResourceCheckAssignments(orgId);
  const templatesQuery = useCheckTemplates(orgId);
  const setAssignments = useSetResourceCheckAssignments();

  const assignmentByResource = useMemo(
    () => new Map((assignmentsQuery.data ?? []).map((a) => [a.resourceId, a])),
    [assignmentsQuery.data],
  );

  // Only published templates can be assigned — an unpublished one would leave
  // the driver with nothing to open, and the BE rejects it anyway.
  const templateOptions = useMemo(
    () =>
      (templatesQuery.data ?? [])
        .filter((x) => x.status === 'Published')
        .map((x) => ({ value: x.id, label: x.name })),
    [templatesQuery.data],
  );

  const bulkAssign = useAssignResourcesHomeSite();
  const [bulkTemplateId, setBulkTemplateId] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  /** Site chosen in the bulk bar. '' = not chosen yet, '__clear__' = unassign. */
  const [bulkSiteId, setBulkSiteId] = useState('');

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // ── Editor handlers ──────────────────────────────────────────────────────

  const startNew = () => {
    setEditingId('new');
    setDraft(emptyDraft());
  };

  const startEdit = (row: Resource) => {
    setEditingId(row.id);
    setDraft(toDraft(row));
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(emptyDraft());
  };

  const requestDelete = (row: Resource) => setPendingDelete(row);

  const handleSave = async () => {
    if (!orgId || editingId === null) return;
    if (!draft.type) return; // type-guard for the wire payload below

    const name = draft.name.trim();
    if (!name) return;

    const payload = {
      type: draft.type,
      name,
      displayName: draft.displayName.trim() || null,
      mobileNumber: draft.mobileNumber.trim() || null,
      email: draft.email.trim() || null,
      reference: draft.reference.trim() || null,
      organisationEquipmentTypeId: draft.organisationEquipmentTypeId || null,
      homeSiteId: draft.homeSiteId || null,
      defaultVehicleResourceId: draft.defaultVehicleResourceId || null,
      status: draft.status,
    };

    try {
      if (editingId === 'new') {
        const body: CreateResourceRequest = { organisationId: orgId, ...payload };
        await createMutation.mutateAsync(body);
        showToast({ type: 'success', message: 'Resource added.' });
      } else {
        const body: UpdateResourceRequest = { id: editingId, ...payload };
        await updateMutation.mutateAsync(body);
        showToast({ type: 'success', message: 'Resource updated.' });
      }
      cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to save resource.',
      });
    }
  };

  const confirmDeletePending = async () => {
    const row = pendingDelete;
    if (!row || !orgId) return;
    try {
      await deleteMutation.mutateAsync({ id: row.id, organisationId: orgId });
      showToast({ type: 'success', message: 'Resource removed.' });
      if (editingId === row.id) cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to delete resource.',
      });
    } finally {
      setPendingDelete(null);
    }
  };

  /**
   * Assign the chosen template to every selected VEHICLE. Drivers in the
   * selection are skipped rather than rejected — a driver performs checks, it
   * is never the subject of one, so silently ignoring them is kinder than
   * refusing the whole batch.
   */
  const applyBulkTemplate = async () => {
    if (!orgId || !bulkTemplateId) return;
    const vehicleIds = visibleResources
      .filter((r) => selectedIds.has(r.id) && VEHICLE_TYPES.includes(r.type))
      .map((r) => r.id);

    if (vehicleIds.length === 0) {
      showToast({ type: 'error', message: t('bulk.noVehiclesSelected') });
      return;
    }

    try {
      const count = await setAssignments.mutateAsync({
        organisationId: orgId,
        resourceIds: vehicleIds,
        checkTemplateId: bulkTemplateId === CLEAR_TEMPLATE ? null : bulkTemplateId,
      });
      showToast({ type: 'success', message: t('bulk.templateDone', { count }) });
      setBulkTemplateId('');
      clearSelection();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error && err.message ? err.message : t('bulk.templateFailed'),
      });
    }
  };

  /** Assign (or clear) the template for a single resource from its row editor. */
  const assignTemplateTo = async (resourceId: string, templateId: string | null) => {
    if (!orgId) return;
    try {
      await setAssignments.mutateAsync({
        organisationId: orgId,
        resourceIds: [resourceId],
        checkTemplateId: templateId,
      });
      showToast({
        type: 'success',
        message: templateId ? t('bulk.templateSet') : t('bulk.templateCleared'),
      });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error && err.message ? err.message : t('bulk.templateFailed'),
      });
    }
  };

  const hasActiveFilters =
    search.trim() !== '' || filterStatus !== 'all';

  const clearFilters = () => {
    setSearch('');
    setFilterStatus('all');
  };

  // ── Derived data ─────────────────────────────────────────────────────────

  const visibleResources = useMemo(() => {
    const all = listQuery.data ?? [];
    const q = search.trim().toLowerCase();
    const filtered = all.filter((r) => {
      // The mode decides the scope; there is no all-resources view any more.
      if (isVehicles ? !VEHICLE_TYPES.includes(r.type) : r.type !== 'Driver') return false;
      if (q) {
        const haystack = [
          r.name,
          r.displayName ?? '',
          r.email ?? '',
          r.mobileNumber ?? '',
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (
        filterStatus !== 'all' &&
        checkSummaryFor(checkSummaries, r.id).status !== filterStatus
      ) {
        return false;
      }
      return true;
    });
    return applySort(filtered, sortKey, sortDir, checkSummaries);
  }, [listQuery.data, search, filterStatus, sortKey, sortDir, checkSummaries, isVehicles]);

  const allVisibleSelected =
    visibleResources.length > 0 && visibleResources.every((r) => selectedIds.has(r.id));

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelectAllVisible = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) for (const r of visibleResources) next.delete(r.id);
      else for (const r of visibleResources) next.add(r.id);
      return next;
    });

  const clearSelection = () => {
    setSelectedIds(new Set());
    setBulkSiteId('');
  };

  /**
   * Assign the chosen site to every selected resource in one request. The BE
   * rejects the whole batch if any id isn't in the org, so this is all-or-nothing
   * rather than a partial write.
   */
  const applyBulkSite = async () => {
    if (!orgId || selectedIds.size === 0 || !bulkSiteId) return;
    try {
      const changed = await bulkAssign.mutateAsync({
        organisationId: orgId,
        resourceIds: [...selectedIds],
        homeSiteId: bulkSiteId === CLEAR_SITE ? null : bulkSiteId,
      });
      showToast({ type: 'success', message: t('bulk.done', { count: changed }) });
      clearSelection();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to assign site.',
      });
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader
        title={isVehicles ? t('vehicles.title') : t('drivers.title')}
        description={isVehicles ? t('vehicles.description') : t('drivers.description')}
      />

      <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto pb-4">
        {!orgId && (
          <ErrorBanner
            title="No organisation linked to this account"
            message="Your access token does not include a Check organisation claim. Contact an administrator."
          />
        )}

        {orgId && listQuery.isPending && (
          <Loading label={isVehicles ? t('vehicles.title') : t('drivers.title')} />
        )}

        {orgId && listQuery.isError && (
          <ErrorBanner title="Failed to load resources" message={listQuery.error.message} />
        )}

        {orgId && listQuery.isSuccess && (
          <>
            {/* ── Filters + search + add button ────────────────────────── */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Status chips — filter by walk-around outcome. Every row reads
                  "Not done" until the Check API lands, so any other selection
                  legitimately empties the grid. */}
              {(['all', ...CHECK_STATUS_ORDER] as StatusFilter[]).map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setFilterStatus(status)}
                  className={`inline-flex items-center gap-1 ${chipClass(
                    filterStatus === status,
                  )}`}
                >
                  {status !== 'all' && (
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${CHECK_STATUS_DOT[status]}`}
                      aria-hidden="true"
                    />
                  )}
                  {status === 'all' ? t('filters.all') : checkStatusLabels[status]}
                </button>
              ))}

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-[10px] text-slate-400 underline hover:text-slate-600"
                >
                  {t('filters.clear')}
                </button>
              )}

              <div className="flex-1" />

              {/* Search */}
              <div className="relative">
                <Search
                  className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  placeholder={t('search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-52 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* +Add — opens the inline editor in 'new' mode. Hidden
                  while another row is being edited so we never have two
                  drafts in flight. Verb comes from common:actions.add so
                  the same word reads identically across every screen. */}
              {editingId === null && (
                <button
                  type="button"
                  onClick={startNew}
                  disabled={isBusy}
                  className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t('common:actions.add')}
                </button>
              )}
            </div>

            {/* ── Bulk site assignment ─────────────────────────────────── */}
            {/* Appears once rows are ticked. One request assigns the site to
                the whole selection — the point of the exercise is not editing
                several hundred imported trailers one at a time. */}
            {selectedIds.size > 0 && (
              <div className="flex flex-wrap items-end gap-3 rounded-md border border-purple-200 bg-purple-50 p-3">
                <span className="pb-1.5 text-sm font-semibold text-slate-800">
                  {t('bulk.selected', { count: selectedIds.size })}
                </span>
                <div className="min-w-[12rem]">
                  <SelectInput
                    label={t('form.site')}
                    name="bulk-site"
                    value={bulkSiteId}
                    disabled={bulkAssign.isPending}
                    options={[
                      { value: '', label: t('form.noSite') },
                      ...sites.map((s) => ({ value: s.id, label: s.name })),
                      { value: CLEAR_SITE, label: t('bulk.unassign') },
                    ]}
                    onChange={(e) => setBulkSiteId(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void applyBulkSite()}
                  disabled={bulkAssign.isPending || !bulkSiteId}
                  className="rounded-md bg-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {bulkAssign.isPending ? `${t('bulk.apply')}…` : t('bulk.apply')}
                </button>
                {isVehicles && (
                  <>
                <span className="h-8 w-px bg-purple-200" aria-hidden="true" />

                {/* Template assignment — vehicles only. */}
                <div className="min-w-[13rem]">
                  <SelectInput
                    label={t('table.template')}
                    name="bulk-template"
                    value={bulkTemplateId}
                    disabled={setAssignments.isPending}
                    options={[
                      { value: '', label: t('bulk.chooseTemplate') },
                      ...templateOptions,
                      { value: CLEAR_TEMPLATE, label: t('bulk.unassignTemplate') },
                    ]}
                    onChange={(e) => setBulkTemplateId(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void applyBulkTemplate()}
                  disabled={setAssignments.isPending || !bulkTemplateId}
                  className="rounded-md bg-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {setAssignments.isPending
                    ? `${t('bulk.applyTemplate')}…`
                    : t('bulk.applyTemplate')}
                </button>
                  </>
                )}

                <button
                  type="button"
                  onClick={clearSelection}
                  disabled={bulkAssign.isPending || setAssignments.isPending}
                  className="rounded-md px-2 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-200 disabled:opacity-50"
                >
                  {t('bulk.clear')}
                </button>
              </div>
            )}

            {/* ── Table ─────────────────────────────────────────────────── */}
            {/* `rounded-md` lives on the scroll container so the rounded
                corners are preserved; the inner div used to carry
                `overflow-hidden rounded-md` together but the
                `overflow-hidden` was clipping any table content that
                grew wider than `min-w-[768px]` (the editor's cert
                grid pushes wider than 768 on viewports between
                ~768–1024). With it clipped, the outer
                `overflow-x-auto` saw nothing past 768px and the user
                couldn't scroll to reach it on mobile. */}
            <div className="overflow-x-auto rounded-md">
              <div className="min-w-[768px]">
                <table className="w-full table-auto text-xs">
                  <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                    <tr>
                      <th className="w-8 px-3 py-2 text-left font-semibold">
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          disabled={visibleResources.length === 0 || bulkAssign.isPending}
                          onChange={toggleSelectAllVisible}
                          aria-label="Select all resources"
                          title="Select all (filtered)"
                          className="h-3.5 w-3.5 rounded border-slate-300"
                        />
                      </th>
                      <SortableTh
                        label={t('table.name')}
                        sortKey="name"
                        active={sortKey}
                        dir={sortDir}
                        onSort={handleSort}
                      />
                      <Th>{t('table.displayName')}</Th>
                      <Th>{t('table.mobile')}</Th>
                      {!isVehicles && <Th>{t('table.email')}</Th>}
                      {isVehicles && (
                        <SortableTh
                          label={t('table.type')}
                          sortKey="type"
                          active={sortKey}
                          dir={sortDir}
                          onSort={handleSort}
                        />
                      )}
                      <Th>{t('table.site')}</Th>
                      {isVehicles && <Th>{t('table.template')}</Th>}
                      {!isVehicles && <Th align="center">{t('table.mobileAccess')}</Th>}
                      <SortableTh
                        label={t('table.lastCheck')}
                        sortKey="lastCheck"
                        active={sortKey}
                        dir={sortDir}
                        onSort={handleSort}
                      />
                      <Th align="center">{t('table.status')}</Th>
                      {/* Sticky-right Actions column — keeps the Edit
                          pencil reachable on mobile (table is wider
                          than the phone viewport). Solid slate-700
                          background so scrolling content can't bleed
                          through during horizontal scroll. */}
                      <th className="sticky right-0 z-10 whitespace-nowrap bg-slate-700 px-3 py-2 text-right font-semibold">
                        {t('table.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-500 bg-white">
                    {/* Pinned new-row editor at the top — same affordance
                        the other settings sections use. */}
                    {editingId === 'new' && orgId && (
                      <tr>
                        <td colSpan={10} className="bg-slate-50 px-3 py-3">
                          <ResourceEditor
                            draft={draft}
                            isNew
                            organisationId={orgId}
                            allowedTypes={isVehicles ? VEHICLE_TYPES : DRIVER_TYPES}
                            templateOptions={templateOptions}
                            assignedTemplateId=""
                            onAssignTemplate={() => undefined}
                            isAssigningTemplate={false}
                            onChange={setDraft}
                            onSave={handleSave}
                            onCancel={cancel}
                            isBusy={isBusy}
                          />
                        </td>
                      </tr>
                    )}

                    {visibleResources.length === 0 && editingId !== 'new' ? (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-3 py-6 text-center text-xs italic text-slate-400"
                        >
                          {hasActiveFilters
                            ? t('table.emptyFiltered')
                            : t('table.empty', { add: t('common:actions.add') })}
                        </td>
                      </tr>
                    ) : (
                      visibleResources.map((r) => {
                        const isEditing = editingId === r.id;
                        return (
                          <Fragment key={r.id}>
                            <ResourceRow
                              resource={r}
                              typeLabel={typeLabels[r.type]}
                              summary={checkSummaryFor(checkSummaries, r.id)}
                              statusLabels={checkStatusLabels}
                              neverLabel={t('table.never')}
                              mobileAccessYesLabel={t('mobileAccess.yes')}
                              mobileAccessNoLabel={t('mobileAccess.no')}
                              isVehicles={isVehicles}
                              assignment={assignmentByResource.get(r.id) ?? null}
                              notSetLabel={t('table.templateNotSet')}
                              naLabel={t('table.templateNotApplicable')}
                              siteName={
                                r.homeSiteId ? (siteNameById.get(r.homeSiteId) ?? null) : null
                              }
                              selected={selectedIds.has(r.id)}
                              onToggleSelect={() => toggleSelect(r.id)}
                              selectDisabled={bulkAssign.isPending}
                              isEditing={isEditing}
                              rowsLocked={editingId !== null && !isEditing}
                              onEdit={() => {
                                if (editingId === null && !isBusy) startEdit(r);
                              }}
                            />
                            {isEditing && orgId && (
                              <tr>
                                <td colSpan={10} className="bg-slate-50 px-3 py-3">
                                  <ResourceEditor
                                    draft={draft}
                                    isNew={false}
                                    organisationId={orgId}
                                    resource={r}
                                    allowedTypes={isVehicles ? VEHICLE_TYPES : DRIVER_TYPES}
                                    templateOptions={templateOptions}
                                    assignedTemplateId={
                                      assignmentByResource.get(r.id)?.checkTemplateId ?? ''
                                    }
                                    onAssignTemplate={(id) => void assignTemplateTo(r.id, id)}
                                    isAssigningTemplate={setAssignments.isPending}
                                            onChange={setDraft}
                                    onSave={handleSave}
                                    onCancel={cancel}
                                    onDelete={() => requestDelete(r)}
                                    isBusy={isBusy}
                                  />
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('delete.title')}
        message={
          pendingDelete
            ? t('delete.message', { name: pendingDelete.displayName || pendingDelete.name })
            : null
        }
        confirmLabel={t('common:actions.delete')}
        onConfirm={confirmDeletePending}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </div>
  );
}

// ── Components ──────────────────────────────────────────────────────────────

function Th({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: 'right' | 'center';
}) {
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th className={`whitespace-nowrap px-3 py-2 font-semibold ${alignClass}`}>{children}</th>
  );
}

function SortableTh({
  label,
  sortKey,
  active,
  dir,
  onSort,
  align,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey | null;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  align?: 'right' | 'center';
}) {
  const isActive = active === sortKey;
  const Icon = isActive ? (dir === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th
      className={`group cursor-pointer select-none whitespace-nowrap px-3 py-2 font-semibold ${alignClass}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon
          className={`h-3 w-3 flex-shrink-0 transition-opacity ${
            isActive ? 'opacity-100' : 'opacity-40 group-hover:opacity-70'
          }`}
          aria-hidden="true"
        />
      </span>
    </th>
  );
}

interface ResourceRowProps {
  resource: Resource;
  // Pre-resolved labels — avoids each row calling useTranslation again.
  typeLabel: string;
  /** Walk-around roll-up for this resource (never null — see checkSummaryFor). */
  summary: ResourceCheckSummary;
  statusLabels: Record<CheckStatusValue, string>;
  /** Shown in Last Check when the resource has never been checked. */
  neverLabel: string;
  mobileAccessYesLabel: string;
  mobileAccessNoLabel: string;
  /** Resolved home Site name, or null when unassigned. */
  siteName: string | null;
  /** The template this resource is checked with, or null when unassigned. */
  assignment: ResourceCheckAssignment | null;
  notSetLabel: string;
  /** Shown for drivers — they perform checks, they are not the subject of one. */
  naLabel: string;
  /** Drives which optional columns this row renders. Must match the header. */
  isVehicles: boolean;
  /** Highlights the row being edited so the editor below is contextual. */
  isEditing: boolean;
  /** Another row is being edited — dim + disable click on this one. */
  rowsLocked: boolean;
  /** Bulk-selection state for the leading checkbox. */
  selected: boolean;
  onToggleSelect: () => void;
  selectDisabled: boolean;
  onEdit: () => void;
}

function ResourceRow({
  resource,
  isVehicles,
  assignment,
  notSetLabel,
  naLabel,
  typeLabel,
  summary,
  statusLabels,
  neverLabel,
  mobileAccessYesLabel,
  mobileAccessNoLabel,
  siteName,
  isEditing,
  rowsLocked,
  selected,
  onToggleSelect,
  selectDisabled,
  onEdit,
}: ResourceRowProps) {
  // Visual states match CargoDefinitionsSection / ChargesAndCostsSection:
  //   - editing: highlighted background.
  //   - rows locked (someone else is editing): dimmed.
  //   - default: hover-able, double-click opens the editor.
  // `group` lets the sticky Actions cell pick up the row hover state
  // via `group-hover:bg-slate-50` — without it the sticky cell would
  // stay solid white through hover and look visually disconnected.
  const rowClass = isEditing
    ? 'group bg-slate-50'
    : rowsLocked
      ? 'group opacity-50'
      : 'group cursor-pointer hover:bg-slate-50';

  return (
    <tr
      className={rowClass}
      onDoubleClick={isEditing || rowsLocked ? undefined : onEdit}
    >
      {/* stopPropagation so ticking a row doesn't also trigger the row's
          double-click-to-edit affordance. */}
      <td className="w-8 px-3 py-1 align-top" onDoubleClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          disabled={selectDisabled}
          onChange={onToggleSelect}
          aria-label={`Select ${resource.displayName || resource.name}`}
          className="h-3.5 w-3.5 rounded border-slate-300"
        />
      </td>
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs font-semibold text-slate-900">
        {resource.name}
      </td>
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs text-slate-600">
        {resource.displayName || <span className="italic text-slate-300">—</span>}
      </td>
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs text-slate-500">
        {resource.mobileNumber || <span className="italic text-slate-300">—</span>}
      </td>
      {!isVehicles && (
        <td className="max-w-[160px] truncate px-3 py-1 align-top text-xs text-slate-500">
          {resource.email || <span className="italic text-slate-300">—</span>}
        </td>
      )}
      {isVehicles && (
        <td className="whitespace-nowrap px-3 py-1 align-top text-xs text-slate-600">
          {typeLabel}
        </td>
      )}
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs text-slate-600">
        {siteName ?? <span className="italic text-slate-300">—</span>}
      </td>
      {isVehicles && (
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs text-slate-600">
        {resource.type === 'Driver' ? (
          <span className="italic text-slate-300">{naLabel}</span>
        ) : assignment ? (
          <span title={assignment.templateName}>{assignment.templateName}</span>
        ) : (
          /* Amber, not grey: a vehicle with no template cannot be checked at
             all, which is a gap worth noticing rather than an empty cell. */
          <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
            {notSetLabel}
          </span>
        )}
      </td>
      )}
      {!isVehicles && (
        <td className="px-3 py-1 align-top text-center">
          {/* Read-only here. Granting access creates a login and belongs in
              Users & Access; this column answers "can this driver submit a
              check at all", which is why an empty Last Check may be empty. */}
          {resource.mobileUserId ? (
            <BadgeCheck
              className="inline-block h-4 w-4 text-emerald-600"
              aria-label={mobileAccessYesLabel}
            />
          ) : (
            <span className="italic text-slate-300" title={mobileAccessNoLabel}>
              —
            </span>
          )}
        </td>
      )}
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs text-slate-600">
        {summary.lastCheckedAt ? (
          formatCheckDate(summary.lastCheckedAt)
        ) : (
          <span className="italic text-slate-300">{neverLabel}</span>
        )}
      </td>
      <td className="px-3 py-1 align-top text-center">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
            CHECK_STATUS_CLASS[summary.status]
          }`}
        >
          {statusLabels[summary.status]}
        </span>
      </td>
      {/* Sticky-right pin — see matching `<th>` comment in the
          parent table. `bg-slate-50` when this row is the one being
          edited (matches the row's highlight); otherwise `bg-white`
          + `group-hover:bg-slate-50` to track the row's default
          hover state. Without an explicit background the scrolling
          content from sibling cells would bleed through. */}
      <td
        className={`sticky right-0 z-10 whitespace-nowrap px-3 py-1 align-top text-right ${
          isEditing ? 'bg-slate-50' : 'bg-white group-hover:bg-slate-50'
        }`}
      >
        <button
          type="button"
          aria-label={`Edit ${resource.name}`}
          disabled={rowsLocked}
          onClick={(e) => {
            e.stopPropagation();
            if (!rowsLocked) onEdit();
          }}
          className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
        >
          <Edit className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function applySort(
  list: Resource[],
  key: SortKey | null,
  dir: SortDir,
  summaries: Map<string, ResourceCheckSummary>,
): Resource[] {
  if (!key) {
    // Default sort: type order then name. Matches the BE's ORDER BY.
    return [...list].sort((a, b) => {
      const ia = TYPE_ORDER.indexOf(a.type);
      const ib = TYPE_ORDER.indexOf(b.type);
      if (ia !== ib) return ia - ib;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }
  const mul = dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    switch (key) {
      case 'name':
        return mul * a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      case 'type': {
        const ia = TYPE_ORDER.indexOf(a.type);
        const ib = TYPE_ORDER.indexOf(b.type);
        return mul * (ia - ib);
      }
      case 'lastCheck': {
        // Never-checked sorts first ascending — "who still needs a check"
        // is the question this column is here to answer.
        const da = checkSummaryFor(summaries, a.id).lastCheckedAt;
        const db = checkSummaryFor(summaries, b.id).lastCheckedAt;
        if (da === db) return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        if (da === null) return -mul;
        if (db === null) return mul;
        return mul * da.localeCompare(db);
      }
    }
  });
}

/**
 * Render an ISO check date in the viewer's locale. Dates only — the
 * grid is a "when was this last looked at" scan, not an audit trail.
 */
function formatCheckDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function chipClass(active: boolean): string {
  return `rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors ${
    active
      ? 'border-slate-700 bg-slate-700 text-white'
      : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700'
  }`;
}
